import { Row, Col } from "antd";
import { AntdCountChart } from "@/components/charts/antd-count-chart";
import { AntdPieChart } from "@/components/charts/antd-pie-chart";
import { AntdBarChart } from "@/components/charts/antd-bar-chart";
import { AntdTableChart } from "@/components/charts/antd-table-chart";
import {
  causeOfDamages,
  dossierTypes,
  preferred,
  sources,
} from "@/lib/formatter/dossiers";
import {
  generatePieChartData,
  generateTopNData,
  generateBarChartData,
  getBarChartDataKeys,
} from "../lib/helpers/dataProcessing";
import { AnalyticsData } from "../types/AnalyticsData.interface";
import { getAnalyticsData } from "../lib/helpers/getAnalyticsData";

const repairAppointmentLabels: Record<string, string> = {
  no: "Geen herstel afspraak",
  yes: "Wel herstel afspraak",
};

export const AnalyticsChartsGrid = ({
  data,
}: {
  data: (AnalyticsData | undefined)[];
}) => {
  const ownership = getAnalyticsData("ownership", data)?.ownershipToCount || {};
  const brand = getAnalyticsData("brand", data)?.brandToCount || {};
  const leaseCompany =
    getAnalyticsData("lease-company", data)?.leaseCompanyToCount || {};
  const hasImages =
    getAnalyticsData("has-images", data)?.hasImagesToCount || {};
  const hasRepairAppointment =
    getAnalyticsData("has-repair-appointment", data)
      ?.hasRepairAppointmentToCount || {};
  const causeOfDamage =
    getAnalyticsData("cause-of-damage", data)?.causeOfDamageToCount || {};
  const servicePartner =
    getAnalyticsData("service-partner", data)?.servicePartnerToCount || {};
  const totalDossiers =
    getAnalyticsData("total-dossiers", data)?.totalDossiersToCount || 0;
  const dossierType =
    getAnalyticsData("dossier-type", data)?.dossierTypeToCount || {};
  const source = getAnalyticsData("source", data)?.sourceToCount || {};
  const servicePartnerChain =
    getAnalyticsData("service-partner-chain", data)
      ?.servicePartnerChainToCount || {};
  const servicePartnerPreferred =
    getAnalyticsData("service-partner-preferred", data)
      ?.preferredServicePartnerCount || {};
  const dealershipLocation =
    getAnalyticsData("dealership-location", data)?.dealershipLocationToCount ||
    {};

  const barChartData = generateBarChartData(servicePartner);
  const barChartDataKeys = getBarChartDataKeys(servicePartner);

  const barChartChainData = generateBarChartData(servicePartnerChain, {
    "-": "Geen keten",
  });
  const barChartChainDataKeys = getBarChartDataKeys(servicePartnerChain);

  return (
    <Row gutter={[24, 24]}>
      {/* Total Count */}
      <Col xs={24} sm={12} md={8} lg={6} xl={6}>
        <AntdCountChart title="Totaal aantal dossiers" count={totalDossiers} />
      </Col>

      {/* Ownership Pie Chart */}
      {Object.keys(ownership).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Type bestuurder"
            data={generatePieChartData(ownership, {
              privatelyOwned: "Verzekeraar",
              leaseCompanyOwned: "Lease",
            })}
          />
        </Col>
      )}

      {/* Brand Pie Chart */}
      {Object.keys(brand).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Merk voertuig"
            data={generatePieChartData(brand)}
          />
        </Col>
      )}

      {/* Lease Company Pie Chart */}
      {Object.keys(leaseCompany).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Opdrachtgever"
            data={generatePieChartData(leaseCompany)}
          />
        </Col>
      )}

      {/* Has Images Pie Chart */}
      {Object.keys(hasImages).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Heeft afbeeldingen"
            data={generatePieChartData(hasImages, { no: "Nee", yes: "Ja" })}
          />
        </Col>
      )}

      {/* Has Repair Appointment Pie Chart */}
      {Object.keys(hasRepairAppointment).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Herstel afspraak"
            data={generatePieChartData(
              hasRepairAppointment,
              repairAppointmentLabels,
            )}
          />
        </Col>
      )}

      {/* Cause of Damage Pie Chart */}
      {Object.keys(causeOfDamage).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Schade oorzaak"
            data={generatePieChartData(causeOfDamage, causeOfDamages)}
          />
        </Col>
      )}

      {/* DossierType Pie Chart */}
      {Object.keys(dossierType).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Dossier type"
            data={generatePieChartData(dossierType, dossierTypes)}
          />
        </Col>
      )}

      {/* Source Pie Chart */}
      {Object.keys(source).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Bron"
            data={generatePieChartData(source, sources)}
          />
        </Col>
      )}

      {/* Dealership Location Pie Chart */}
      {Object.keys(dealershipLocation).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Dealer vestiging"
            data={generatePieChartData(dealershipLocation)}
          />
        </Col>
      )}

      {/* Preferred ServicePartner Pie Chart */}
      {Object.keys(servicePartnerPreferred).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdPieChart
            title="Preferred partner"
            data={generatePieChartData(servicePartnerPreferred, preferred)}
          />
        </Col>
      )}

      {/* Top Tables */}
      {Object.keys(causeOfDamage).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdTableChart
            title="Top schadeoorzaken"
            data={generateTopNData(causeOfDamage, 5, causeOfDamages)}
          />
        </Col>
      )}

      {Object.keys(brand).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdTableChart
            title="Top merken"
            data={generateTopNData(brand, 5)}
          />
        </Col>
      )}

      {Object.keys(leaseCompany).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdTableChart
            title="Top leasebedrijven"
            data={generateTopNData(leaseCompany, 5)}
          />
        </Col>
      )}

      {Object.keys(dealershipLocation).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdTableChart
            title="Top dealer vestigingen"
            data={generateTopNData(dealershipLocation, 5)}
          />
        </Col>
      )}

      {Object.keys(servicePartner).length > 0 && (
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <AntdTableChart
            title="Top herstellers"
            data={generateTopNData(servicePartner, 5)}
          />
        </Col>
      )}

      {/* Bar Chart - full width */}
      {Object.keys(barChartData).length > 0 && (
        <Col span={24}>
          <AntdBarChart
            title="Afspraken per hersteller"
            data={barChartData}
            dataKeys={barChartDataKeys}
          />
        </Col>
      )}

      {/* Bar Chart - full width */}
      {Object.keys(barChartChainData).length > 0 && (
        <Col span={24}>
          <AntdBarChart
            title="Afspraken per keten"
            data={barChartChainData}
            dataKeys={barChartChainDataKeys}
          />
        </Col>
      )}
    </Row>
  );
};
